import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { HeroSection } from "@/components/HeroSection";
import { ServicesPreview } from "@/components/ServicesPreview";
import { PortfolioCarousel } from "@/components/PortfolioCarousel";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { useTranslation } from "react-i18next";

import { LocalizedService, LocalizedPortfolioAlbum, ServiceCategory } from "@/types";

interface PhotographerProfile {
  id: string;
  display_name: string;
  tagline: string;
  location: string;
  phone?: string;
  email?: string;
  background_image_url: string;
  logo_url?: string;
  currency?: string;
}

type Service = LocalizedService;
type PortfolioAlbum = LocalizedPortfolioAlbum;

interface Review {
  id: string;
  rating: number;
  comment: string;
  reviewer_name: string;
  created_at: string;
}

const Index = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<PhotographerProfile | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [portfolioAlbums, setPortfolioAlbums] = useState<PortfolioAlbum[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 }); // Added stats state

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch photographer profile
        const { data: profileData } = await supabase
          .from('photographer_profiles')
          .select('*')
          .limit(1)
          .single();

        // Fetch categories
        const { data: categoriesData } = await supabase
          .from('service_categories')
          .select('*')
          .eq('is_active', true);

        // Fetch services
        const { data: servicesData } = await supabase
          .from('services')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: false })
          .limit(6);

        // Fetch portfolio albums with media
        const { data: portfolioData } = await supabase
          .from('portfolio_albums')
          .select(`
            *,
            portfolio_media (
              id,
              media_url,
              caption,
              sort_order
            )
          `)
          .eq('is_featured', true)
          .order('sort_order');

        // Fetch reviews
        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('*')
          .eq('is_approved', true)
          .order('created_at', { ascending: false })
          .limit(50);

        // Calculate stats
        const { data: allRatings } = await supabase
          .from('reviews')
          .select('rating')
          .eq('is_approved', true);

        if (allRatings) {
          const total = allRatings.length;
          const sum = allRatings.reduce((acc, curr) => acc + curr.rating, 0);
          const average = total > 0 ? Number((sum / total).toFixed(1)) : 0;
          setReviewStats({ average, total });
        }

        setProfile(profileData);
        setCategories(categoriesData || []);
        setServices(servicesData || []);
        setPortfolioAlbums(portfolioData || []);
        setReviews(reviewsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle scroll-to navigation from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[hsl(var(--gold))] mb-3 sm:mb-4 mx-auto"></div>
          <p className="text-sm sm:text-base text-muted-foreground">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  // Default data if no profile exists
  const defaultProfile: PhotographerProfile = {
    id: '',
    display_name: "Amadeo Photography",
    tagline: "Capturing Life's Most Beautiful Moments",
    location: "New York, NY",
    phone: "+1 (555) 123-4567",
    email: "hello@amadeophoto.com",
    background_image_url: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    logo_url: undefined,
    currency: 'USD'
  };

  const currentProfile = profile || defaultProfile;

  return (
    <div className="min-h-screen bg-background">
      <Navigation
        photographerName={currentProfile.display_name}
        logoUrl={currentProfile.logo_url}
      />

      <main>
        <section id="home">
          <HeroSection
            backgroundImage={currentProfile.background_image_url}
            displayName={currentProfile.display_name}
            tagline={currentProfile.tagline}
            location={currentProfile.location}
            rating={reviewStats.average}
            reviewCount={reviewStats.total}
          />
        </section>

        <section id="services">
          <ServicesPreview services={services} categories={categories} />
        </section>

        <section id="portfolio">
          <PortfolioCarousel portfolioItems={portfolioAlbums} />
        </section>

        <section id="reviews">
          <TestimonialsSection
            reviews={reviews}
            averageRating={reviewStats.average}
            totalReviews={reviewStats.total}
          />
        </section>

        <section id="contact">
          <ContactSection />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
